import * as React from "react";
import { Box, Fab, Tooltip, IconButton } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import { colors, tokens } from "../../utils";
import {
  PHONE_TAB_WIDTH,
  PHONE_TAB_HEIGHT,
  PHONE_DISMISS_HEIGHT,
} from "./fabPosition";

// FAB + dismiss-badge geometry, named so the proportion is asserted by a test
// rather than being loose literals a review has to eyeball.
//
// Gordon 2026-08-12 (screenshot): the dismiss "X" badge read as OVERSIZED —
// measured live on web-stg (main.1e3aab37.js) it was a 32px circle on a 56px
// FAB (57% of the FAB), dominating rather than reading as a small close
// affordance. The fix keeps the 32px INTERACTIVE hit area (the app touch-target
// floor, DL-096 / ISP-4021 — never shrink that) but paints a smaller VISIBLE
// badge inside it, so the affordance is small while the target stays accessible.
export const FAB_SIZE = 56;
export const DISMISS_HIT_SIZE = 32; // interactive target — touch-target floor
export const DISMISS_BADGE_SIZE = 20; // visible circle — a small close affordance
export const DISMISS_X_ICON_SIZE = 12; // the glyph inside the visible badge

/**
 * Persistent floating help launcher (SPEC-v1 §7 "a persistent help launcher").
 * Toggles the panel open/closed. 56px FAB (> 44px touch target), navy brand
 * fill, visible keyboard focus ring.
 *
 * Gordon asked three times (2026-07-30, x2) for this launcher to be MOVEABLE
 * and DISMISSABLE, so it now carries two extra affordances:
 *
 *  - DRAG: the whole FAB is the drag handle. `dragHandlers` come from
 *    useFabDrag (pointer events + arrow keys); this component stays
 *    presentational and never owns the position.
 *  - DISMISS: a line-art X in its own 32px hit area at the FAB's top-right.
 *    It is ALWAYS visible rather than hover-revealed — this is the third time
 *    the dismissal has been asked for, and a control you must discover by
 *    hovering is a control most users never find. It stops propagation so a
 *    press on the X never starts a drag or toggles the panel.
 *
 * Dismissal is not a dead end: while dismissed, the avatar/Profile menu grows a
 * "Help" entry that brings the launcher back (see components/ProfileMenu).
 */
/**
 * This FAB's resting elevation. Named so the focus rule can put it back: the
 * app-wide focus treatment paints its white inner band with `box-shadow`, and a
 * shadow list cannot be appended to in CSS, so an elevated surface that does not
 * re-state its own shadow loses it for the duration of the focus. This is the
 * worked example the `--iy-focus-ring-keep` escape hatch in
 * `src/styles/focusRing.css` exists for.
 */
const FAB_ELEVATION = "0 6px 18px rgba(18,48,81,0.32)";

/** The dismiss control's own resting elevation, same reasoning as above. */
const DISMISS_ELEVATION = "0 2px 6px rgba(18,48,81,0.24)";

export default function HelpLauncher({
  open,
  onToggle,
  onDismiss,
  dragHandlers = {},
  dragging = false,
  reduceMotion = false,
  compact = false,
}) {
  // T22.227: on a phone the launcher is a flush right-edge tab — the dismiss
  // X's 32px hit area stacked ON TOP of a 32x48 help tab, one 32px-wide column
  // (geometry in fabPosition.js's launcherBoxForWidth) — instead of a 56px
  // round FAB with the X overhanging its corner.
  const wrapperStyle = compact
    ? {
        position: "relative",
        width: PHONE_TAB_WIDTH,
        height: PHONE_DISMISS_HEIGHT + PHONE_TAB_HEIGHT,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }
    : { position: "relative", width: 56, height: 56 };
  const launcherSize = compact
    ? {
        width: PHONE_TAB_WIDTH,
        height: PHONE_TAB_HEIGHT,
        minHeight: 0,
        borderRadius: "12px 0 0 12px",
      }
    : { width: 56, height: 56 };
  const dismissPlacement = compact
    ? { position: "relative", flex: "0 0 auto" }
    : {
        position: "absolute",
        // Anchor the (now smaller) visible badge to the FAB's top-right
        // corner. The 32px hit area is centred on the badge, so it overhangs
        // the corner symmetrically.
        top: -DISMISS_HIT_SIZE / 2 + DISMISS_BADGE_SIZE / 2 - 4,
        right: -DISMISS_HIT_SIZE / 2 + DISMISS_BADGE_SIZE / 2 - 4,
      };
  return (
    <div data-testid="help-launcher" data-compact={compact ? "true" : "false"} style={wrapperStyle}>
      <Tooltip
        title={open ? "Close help" : "Ask the Help Assistant — drag to move"}
        placement="left"
      >
        <Fab
          aria-label={open ? "Close help assistant" : "Open help assistant"}
          aria-expanded={open}
          onClick={onToggle}
          {...dragHandlers}
          sx={{
            ...launcherSize,
            // The phone tab paints below the X stacked above it.
            ...(compact ? { order: 2 } : {}),
            backgroundColor: colors.primary,
            color: colors.white,
            boxShadow: FAB_ELEVATION,
            // The FAB is its own drag handle; `touch-action: none` stops a touch
            // drag from scrolling the page instead of moving the button.
            touchAction: "none",
            cursor: dragging ? "grabbing" : "grab",
            "&:hover": { backgroundColor: colors.PrimaryPurple700 },
            // THE app-wide double ring, read from the token — never re-typed.
            // Measured on live staging 2026-08-02 (bundle main.cfc59c6c.js): this
            // FAB is filled #123051, where the white inner band measures 13.40:1,
            // and the purple outer band measures 3.76:1 against the #F6F7F9 page.
            // Both boundaries clear WCAG 2.2 SC 1.4.11's 3:1 floor.
            //
            // The width also moves 3px -> 2px here: it was the only 3px ring in the
            // measured header cluster, and one geometry app-wide is the point.
            "&:focus-visible": {
              outline: `${tokens.focusRing.width}px solid ${tokens.focusRing.color}`,
              outlineOffset: tokens.focusRing.innerWidth,
              boxShadow: `${tokens.focusRing.innerShadow}, ${FAB_ELEVATION}`,
            },
          }}
        >
          {open ? (
            <CloseIcon sx={{ fontSize: compact ? 20 : 26 }} />
          ) : (
            <HelpOutlineIcon sx={{ fontSize: compact ? 22 : 28 }} />
          )}
        </Fab>
      </Tooltip>

      <Tooltip title="Hide help — restore it from your profile menu" placement="top">
        <IconButton
          aria-label="Hide help assistant"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            if (onDismiss) onDismiss();
          }}
          // Keep a press on the X out of the drag machinery entirely, so the
          // dismiss control can never be mistaken for the start of a drag.
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          sx={{
            ...dismissPlacement,
            ...(compact ? { order: 1 } : {}),
            // QA-111 (Kelly, measured live on web-stg 2026-08-06): MUI's Fab
            // applies `theme.zIndex.fab` (1050) to its own root by default.
            // This X sits inside the SAME 56x56 relative wrapper as that Fab and
            // overlaps the Fab's square corner, so without an explicit z-index
            // here the Fab (1050, positioned) painted ABOVE this button and
            // silently absorbed real clicks. One above the Fab's z-index wins.
            zIndex: 1051,
            // The 32px INTERACTIVE hit area (touch-target floor DL-096/ISP-4021)
            // is preserved and made transparent; the visible chrome lives on the
            // smaller inner badge below.
            width: DISMISS_HIT_SIZE,
            height: DISMISS_HIT_SIZE,
            padding: 0,
            backgroundColor: "transparent",
            "&:hover": { backgroundColor: "transparent" },
            "&:hover .help-dismiss-badge": { backgroundColor: colors.PrimaryPurple100 },
            "&:focus-visible .help-dismiss-badge": {
              outline: `${tokens.focusRing.width}px solid ${tokens.focusRing.color}`,
              outlineOffset: tokens.focusRing.innerWidth,
            },
          }}
        >
          {/* The VISIBLE badge — a small close affordance (Gordon 2026-08-12),
              painted inside the larger transparent hit target above. */}
          <Box
            className="help-dismiss-badge"
            sx={{
              width: DISMISS_BADGE_SIZE,
              height: DISMISS_BADGE_SIZE,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: colors.white,
              color: colors.primary,
              border: `1px solid ${tokens.focusRing.color}`,
              boxShadow: DISMISS_ELEVATION,
              transition: reduceMotion ? "none" : "background-color 120ms ease-out",
            }}
          >
            <CloseIcon sx={{ fontSize: DISMISS_X_ICON_SIZE }} />
          </Box>
        </IconButton>
      </Tooltip>
    </div>
  );
}
